import { useEffect, useMemo, useState } from 'react';
import type { Customer } from '@visionqc/shared';
import { api } from '../api/client';
import { CameraConnection, ZoneEditor, type ZoneRule } from '../components/liveCameras';

/**
 * Guided safety-scenario setup.
 *
 * Configuring what to detect, on which camera, where, and how you're alerted used
 * to mean visiting three different screens in the right order and knowing which
 * detector maps to which rule. This wizard does it as one guided flow —
 * scenario → camera → zone → alerts → review — so a scenario is faster to set up
 * and much harder to get wrong. It writes through the existing safety endpoints
 * and reuses the same camera-connection and zone-drawing components used
 * elsewhere, so nothing here is a second, drifting copy.
 */

interface Scenario {
  key: string;
  label: string;
  desc: string;
  kinds: string[];
  /** Whether this scenario is defined by a drawn area. */
  zone: 'required' | 'optional' | 'none';
  zoneRule: ZoneRule | null;
}

const SCENARIOS: Scenario[] = [
  { key: 'ppe', label: 'PPE compliance', desc: 'Hard hats and hi-vis vests on people in view.', kinds: ['person', 'ppe'], zone: 'optional', zoneRule: 'ppe' },
  { key: 'fire', label: 'Smoke & fire', desc: 'Smoke or flames anywhere in the frame.', kinds: ['fire'], zone: 'none', zoneRule: null },
  { key: 'forklift', label: 'Forklift near-miss', desc: 'People and forklifts, to flag close calls.', kinds: ['person', 'forklift'], zone: 'optional', zoneRule: 'no-vehicles' },
  { key: 'restricted', label: 'Restricted zone', desc: 'Nobody may enter a marked area.', kinds: ['person'], zone: 'required', zoneRule: 'no-entry' },
  { key: 'occupancy', label: 'Occupancy cap', desc: 'No more than N people in an area at once.', kinds: ['person'], zone: 'required', zoneRule: 'occupancy' },
];

interface Cam { id: number; name: string; source: string; customerId?: number | null; referenceKey?: string | null }

export function SafetyScenarioWizard({
  onClose,
  onDone,
  apiBase = '/api/safety',
  portal = false,
}: {
  onClose: () => void;
  onDone: () => void;
  /** '/api/safety' for staff, '/api/portal/safety' for a tenant's own setup. */
  apiBase?: string;
  /** Portal mode: no customer picker, no physical-alarm step (staff-only). */
  portal?: boolean;
}) {
  const A = apiBase;
  const [step, setStep] = useState(1);
  const [scenario, setScenario] = useState<Scenario | null>(null);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [cameras, setCameras] = useState<Cam[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [camId, setCamId] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [addingCam, setAddingCam] = useState(false);

  const [alarmOn, setAlarmOn] = useState(false);
  const [alarmTargets, setAlarmTargets] = useState<Array<Record<string, unknown>>>([]);
  const [alarmTested, setAlarmTested] = useState<string | null>(null);
  const [blur, setBlur] = useState(true);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [snapTick, setSnapTick] = useState(Date.now());

  const loadCameras = () =>
    fetch(`${A}/cameras`).then((r) => (r.ok ? r.json() : [])).then(setCameras).catch(() => undefined);

  useEffect(() => {
    loadCameras();
    if (portal) return;
    api.listCustomers().then(setCustomers).catch(() => undefined);
    fetch(`${A}/alarm-config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        const t: Array<Record<string, unknown>> = c?.targets ?? [];
        setAlarmTargets(t);
        setAlarmOn(t.some((x) => x.enabled));
      })
      .catch(() => undefined);
  }, []);

  const cam = useMemo(() => cameras.find((c) => c.id === camId) ?? null, [cameras, camId]);
  const needsZone = scenario ? scenario.zone !== 'none' : false;
  // The zone step only shows for zone-defined scenarios; optional-zone scenarios
  // get a "whole camera" skip inside that step.
  const steps = useMemo(
    () => ['Scenario', 'Camera', ...(needsZone ? ['Zone'] : []), ...(portal ? [] : ['Alerts']), 'Review'],
    [needsZone, portal],
  );
  const stepName = steps[step - 1];

  const addCamera = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${A}/cameras`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(portal ? { name: newName.trim(), source: 'upload' } : { name: newName.trim(), source: 'upload', customerId }),
      });
      if (!res.ok) throw new Error('Could not add the camera');
      const { id } = (await res.json()) as { id: number };
      await loadCameras();
      setCamId(id);
      setAddingCam(false);
      setNewName('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const testAlarm = async () => {
    setAlarmTested('…');
    try {
      const res = await fetch(`${A}/alarm-config/test`, { method: 'POST' });
      setAlarmTested(res.ok ? 'Fired — check the siren/lamp on site.' : 'No physical alarm is configured yet.');
    } catch {
      setAlarmTested('Could not reach the alarm.');
    }
  };

  const finish = async () => {
    if (!scenario || !cam) return;
    setBusy(true);
    setError(null);
    try {
      // The detectors + tenant + blur + go-live, in one write. Zones were saved
      // by the ZoneEditor in its own step.
      const res = await fetch(`${A}/cameras/${cam.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(portal ? { kinds: scenario.kinds, privacyBlur: blur, active: true } : { kinds: scenario.kinds, customerId, privacyBlur: blur, active: true }),
      });
      if (!res.ok) throw new Error('Could not apply the scenario');
      // Physical alarm is a set of webhook targets; flip them all to the chosen
      // state. With none configured there is nothing to enable — handled in the UI.
      if (alarmTargets.length > 0) {
        await fetch(`${A}/alarm-config`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ targets: alarmTargets.map((t) => ({ ...t, enabled: alarmOn })) }),
        }).catch(() => undefined);
      }
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const canNext =
    (stepName === 'Scenario' && !!scenario) ||
    (stepName === 'Camera' && !!cam && cam.source !== 'upload') ||
    stepName === 'Zone' ||
    stepName === 'Alerts';

  const snapUrl = cam ? `${A}/cameras/${cam.id}/snapshot?ts=${snapTick}` : '';

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 24, zIndex: 50, overflow: 'auto' }}
      onClick={busy ? undefined : onClose}
    >
      <div className="panel" style={{ maxWidth: 720, width: '100%', margin: 'auto' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="New safety scenario">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0 }}>New safety scenario</h2>
          <span className="muted" style={{ fontSize: 12 }}>Step {step} of {steps.length}</span>
        </div>

        {/* Step rail */}
        <div className="row" style={{ gap: 6, margin: '12px 0 18px', flexWrap: 'wrap' }}>
          {steps.map((lbl, i) => {
            const n = i + 1;
            const done = n < step;
            const active = n === step;
            return (
              <div key={lbl} style={{ flex: 1, minWidth: 90, textAlign: 'center', fontSize: 12, fontWeight: 600, padding: '6px 4px', borderRadius: 6, background: active ? 'var(--accent)' : done ? '#2e7d32' : 'transparent', color: active || done ? '#fff' : 'var(--muted)', border: '1px solid var(--border)' }}>
                {done ? '✓ ' : ''}{lbl}
              </div>
            );
          })}
        </div>

        {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {notice && <div style={{ color: 'var(--good)', fontSize: 13, marginBottom: 12 }}>{notice}</div>}

        {stepName === 'Scenario' && (
          <div className="grid" style={{ gap: 10 }}>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>What should this camera watch for?</p>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 10 }}>
              {SCENARIOS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setScenario(s)}
                  className="panel"
                  style={{ textAlign: 'left', cursor: 'pointer', border: `1px solid ${scenario?.key === s.key ? 'var(--accent)' : 'var(--border)'}`, background: scenario?.key === s.key ? 'var(--accent-dim)' : undefined }}
                >
                  <div style={{ fontWeight: 700 }}>{s.label}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{s.desc}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                    Detects: {s.kinds.join(', ')}{s.zone === 'required' ? ' · needs a drawn area' : ''}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {stepName === 'Camera' && (
          <div className="grid" style={{ gap: 12 }}>
            {!portal && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Customer</label>
                <select value={customerId ?? ''} onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : null)} style={{ width: '100%' }}>
                  <option value="">— unassigned (staff only) —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>The scenario appears in this customer's portal. Leave unassigned to keep it staff-only.</p>
              </div>
            )}

            <div>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Camera</label>
              {!addingCam ? (
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <select value={camId ?? ''} onChange={(e) => setCamId(e.target.value ? Number(e.target.value) : null)} style={{ flex: 1 }}>
                    <option value="">— pick a camera —</option>
                    {cameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button className="btn sm" onClick={() => setAddingCam(true)}>+ Add camera</button>
                </div>
              ) : (
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <input autoFocus placeholder='e.g. "Loading bay — east"' value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) void addCamera(); if (e.key === 'Escape') setAddingCam(false); }} style={{ flex: 1 }} />
                  <button className="btn sm primary" disabled={!newName.trim() || busy} onClick={() => void addCamera()}>Create</button>
                  <button className="btn sm" onClick={() => setAddingCam(false)}>Cancel</button>
                </div>
              )}
            </div>

            {cam && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Connect the live feed</label>
                <CameraConnection
                  camera={{ id: cam.id, source: cam.source }}
                  apiBase={A}
                  onChanged={() => { void loadCameras(); setSnapTick(Date.now()); }}
                  onNotice={(m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); }}
                  onError={setError}
                />
                {cam.source !== 'upload' && (
                  <div style={{ marginTop: 8 }}>
                    <img src={snapUrl} alt="live" style={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 8, background: '#000' }} onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden')} />
                  </div>
                )}
                <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>Set an RTSP/HTTP source (or auto-detect), then grab a frame — you'll draw the zone on it next.</p>
              </div>
            )}
          </div>
        )}

        {stepName === 'Zone' && cam && (
          <div className="grid" style={{ gap: 10 }}>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              Outline the area, then it starts on the <b>{scenario?.zoneRule}</b> rule for this scenario. You can add more areas or change the rule per area.
            </p>
            <ZoneEditor
              cameraId={cam.id}
              imageUrl={snapUrl}
              apiBase={A}
              defaultRule={scenario?.zoneRule ?? undefined}
              onSaved={(n) => { setNotice(`${n} zone${n === 1 ? '' : 's'} saved.`); setTimeout(() => setNotice(null), 2500); }}
              onError={setError}
            />
            {scenario?.zone === 'optional' && (
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>Optional for this scenario — skip to watch the whole camera.</p>
            )}
          </div>
        )}

        {stepName === 'Alerts' && (
          <div className="grid" style={{ gap: 12 }}>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>Events are always recorded and shown on the dashboard. You can also sound a physical alarm on a critical event.</p>
            <label className="row" style={{ gap: 8, alignItems: 'center', cursor: alarmTargets.length ? 'pointer' : 'not-allowed', opacity: alarmTargets.length ? 1 : 0.55 }}>
              <input type="checkbox" checked={alarmOn} disabled={alarmTargets.length === 0} onChange={(e) => setAlarmOn(e.target.checked)} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Sound the site alarm on a critical event</span>
            </label>
            {alarmTargets.length === 0 ? (
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>No physical alarm is configured yet. Add a siren/lamp/relay under <b>Safety → Zones → physical alarm</b>, then it becomes available here.</p>
            ) : (
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <button className="btn sm" onClick={() => void testAlarm()}>Test the alarm</button>
                {alarmTested && <span className="muted" style={{ fontSize: 12 }}>{alarmTested}</span>}
              </div>
            )}
          </div>
        )}

        {stepName === 'Review' && scenario && cam && (
          <div className="grid" style={{ gap: 12 }}>
            <div className="panel" style={{ background: 'transparent' }}>
              <Row k="Scenario" v={scenario.label} />
              <Row k="Detects" v={scenario.kinds.join(', ')} />
              <Row k="Camera" v={cam.name} />
              {!portal && <Row k="Customer" v={customers.find((c) => c.id === customerId)?.name ?? 'Unassigned'} />}
              {needsZone && <Row k="Area" v="drawn on the previous step" />}
              <Row k="Site alarm" v={alarmOn ? 'On (critical events)' : 'Off'} />
            </div>
            <label className="row" style={{ gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={blur} onChange={(e) => setBlur(e.target.checked)} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Blur faces for privacy</span>
            </label>
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>Finishing turns the camera on and starts monitoring for this scenario.</p>
          </div>
        )}

        {/* Footer */}
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 20 }}>
          <button className="btn" onClick={busy ? undefined : onClose} disabled={busy}>Cancel</button>
          <div className="row" style={{ gap: 8 }}>
            {step > 1 && <button className="btn" onClick={() => setStep((s) => s - 1)} disabled={busy}>← Back</button>}
            {step < steps.length ? (
              <button className="btn primary" disabled={!canNext} onClick={() => setStep((s) => s + 1)}>
                {stepName === 'Zone' && scenario?.zone === 'optional' ? 'Skip / Next →' : 'Next →'}
              </button>
            ) : (
              <button className="btn primary" disabled={busy} onClick={() => void finish()}>{busy ? 'Applying…' : 'Finish & start'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
      <span className="muted" style={{ fontSize: 13 }}>{k}</span>
      <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{v}</span>
    </div>
  );
}
